import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular';
import { applicationTypeNotForMeJobButtonText, applicationTypeToGetThisJobButtonText } from 'src/app/models/job-application';
import { RejectJobConfirmationComponent } from '../reject-job-confirmation/reject-job-confirmation.component';
import { SegmentCMOSService } from 'src/app/services/segment-cmos.service';
import { UserService } from 'src/app/services/user.service';
import { UrlHistoryService } from 'src/app/services/url-history.service';
import { ScheduleConfirmationComponent } from '../schedule-confirmation/schedule-confirmation.component';
import { ApiV1Job } from '@bluecrew/crew-member-backend-angular-client';
import { Blueshift, MultiSelectComponent, PositionDetail } from '../multi-select/multi-select.component';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';

@Component({
  selector: 'app-get-this-job',
  templateUrl: './get-this-job.component.html',
  styleUrls: ['./get-this-job.component.scss'],
})
export class GetThisJobComponent implements OnInit {

  @Input() jobExternalId: string;
  @Input() applicationType: string;
  @Input() job: ApiV1Job;
  @Input() blueshifts: ApiV1Job[];
  @Input() isBlueshiftCardSelected?: boolean = false;
  @Input() isAuthenticatedUser?: boolean = false;
  @Input() hasMultipleSchedules?: boolean = false;
  @Output() notForMeEvent = new EventEmitter<any>();

  constructor(private readonly modalCtrl: ModalController,
              private readonly navCtrl: NavController,
              private readonly urlHistory: UrlHistoryService,
              private readonly segmentCMOS: SegmentCMOSService,
              private readonly userService: UserService) { }

  ngOnInit() { }

  getJobButtonText() {
    if (this.isBlueshiftCardSelected) {
      return 'Pick your shifts';
    } else {
      return applicationTypeToGetThisJobButtonText[this.applicationType] || 'Get this job';
    }
  }

  async notForMe() {
    if (this.isAuthenticatedUser) {
      const eventEmitter = new EventEmitter();
      const m = await this.modalCtrl.create({
        component: RejectJobConfirmationComponent,
        cssClass: 'center-modal',
        componentProps: {
          // if Blueshift, use the first shift's jobId for rejection
          jobExternalId: this.isBlueshiftCardSelected ? this.blueshifts[0].legacyExternalJobId : this.jobExternalId,
          isBlueshift: this.isBlueshiftCardSelected,
          notForMeEvent: eventEmitter
        },
      });
      eventEmitter.subscribe(() => {
        this.notForMeEvent.emit();
      });
      await m.present();
    } else {
      this.urlHistory.goBackWithDefault();
    }
  }

  getNotForMeText() {
    if (this.isAuthenticatedUser) {
      return applicationTypeNotForMeJobButtonText[this.applicationType] || 'Not for me';
    } else {
      return 'Find more jobs';
    }
  }

  async getThisJob() {
    if (this.isBlueshiftCardSelected) {
      const multiSelectId = 'multi-select-modal';
      const m = await this.modalCtrl.create({
        component: MultiSelectComponent,
        cssClass: 'fullscreen-modal',
        componentProps: {
          blueshiftsInput: this.convertBlueshiftsFromApiV1Jobs(this.blueshifts),
          positionDetail: this.getPositionDetailFromApiV1Jobs(this.blueshifts),
          modalId: multiSelectId
        },
        id: multiSelectId
      });
      await m.present();
    } else {
      const eventEmitter = new EventEmitter();
      const m = await this.modalCtrl.create({
        component: ScheduleConfirmationComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: {
          schedule: this.job,
          confirmEvent: eventEmitter
        }
      });
      eventEmitter.subscribe(() => {
        this.directUserToJob();
      });
      await m.present();
    }
  }

  directUserToJob() {
    if (this.userService.currentUser) {
      this.segmentCMOS.addEvent('acceptJob', { jobId: this.jobExternalId });
      this.navCtrl.navigateBack(`/application-home?externalId=${this.jobExternalId}`);
    } else {
      this.segmentCMOS.addEvent('anonAcceptJob', { jobId: this.jobExternalId });
      this.userService.setJobId(this.jobExternalId);
      this.navCtrl.navigateRoot(AppRoutingUtil.ACCOUNT_REGISTER);
    }
  }

  private convertBlueshiftsFromApiV1Jobs(blueshifts: ApiV1Job[]): Blueshift[] {
    return blueshifts.map((job) => {
      return {
        doesConflictWithExistingJob: !!(job.overlappingJobIds && job.overlappingJobIds.length > 0),
        externalJobId: job.legacyExternalJobId,
        startDateTime: job.startTime,
        endDateTime: job.endTime,
        wage: job.wage,
        isDirectInvite: job.type.toUpperCase() === 'DIRECTINVITE'
      };
    });
  }

  private getPositionDetailFromApiV1Jobs(blueshifts: ApiV1Job[]): PositionDetail {
    const job = blueshifts[0];
    return {
      companyName: job.companyName,
      positionId: job.positionId,
      positionTitle: job.title
    }
  }
}
